import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/shared/components/ui/forms/form'
import type { PlaceResult } from 'src/shared/components/ui/forms/location-autocomplete'
import { LocationAutoComplete } from 'src/shared/components/ui/forms/location-autocomplete'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useEventStore } from 'src/shared/store/event-store'

const generalLocationsSchema = z.object({
  desiredLocations: z.array(z.string()).min(1, 'At least one desired location is required'),
})

export const GeneralLocationsForm = () => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const form = useForm<z.infer<typeof generalLocationsSchema>>({
    resolver: zodResolver(generalLocationsSchema),
    mode: 'onSubmit',
    defaultValues: {
      desiredLocations: (referralFormData.desiredLocations as string[]) || [],
    },
  })

  const { append, remove } = useFieldArray({
    control: form.control,
    // @ts-ignore: desiredLocations is causing a never type error
    name: 'desiredLocations',
  })

  const handlePlaceSelected = (place: PlaceResult) => {
    if (place.city && place.state) {
      const location = `${place.city}, ${place.state}`
      append(location.trim())
    }
  }

  const onSubmit = async (data: z.infer<typeof generalLocationsSchema>) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_general_locations',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="desiredLocations"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Desired Locations</FormLabel>
              <FormControl>
                <Controller
                  name="desiredLocations"
                  control={form.control}
                  render={() => (
                    <LocationAutoComplete
                      onPlaceSelected={handlePlaceSelected}
                      types={['(cities)']}
                      componentRestrictions={{ country: 'us' }}
                      refValue=""
                      placeholder="City, State"
                    />
                  )}
                />
              </FormControl>
              <div className="mt-2 flex flex-wrap gap-2">
                {field.value.map((location, index) => (
                  <Badge key={index} variant="secondary" text={location} showCloseIcon onClick={() => remove(index)} />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
